import Add16 from '@carbon/icons-react/es/add/16';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { Goal, GoalsApi } from 'api/goal';
import { ProjectId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { RichTextareaEditor } from 'components/atoms/form/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { ActionBar } from 'components/molecules/action-bar';
import { toastService } from 'components/molecules/toast';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';

const Footer = styled(ActionBar)`
  background: var(--white-100);
`;

export const GoalModal: FC<{
  data?: Partial<Goal>;
  visible?: boolean;
  projectId: number;
  onClose(): void;
}> = ({ data, visible, projectId, onClose }) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const errorTitle = useTranslation('error_saving');
  const errorMessage = useTranslation('error');
  const goalLabel = useTranslation('goal');
  const goalErrorMessage = useTranslation(
    'project.overview.business_goals.validations.goalName',
  );
  const goalPlaceholder = useTranslation(
    'project.overview.business_goals.placeholder.goalName',
  );
  const goalDescriptionLabel = useTranslation('description');
  const goalDescriptionErrorMessage = useTranslation(
    'project.overview.business_goals.validations.goalDescription',
  );
  const goalDescriptionPlaceholder = useTranslation(
    'project.overview.business_goals.placeholder.goalDescription',
  );

  const { mutate: onSave } = useMutation((values: Partial<Goal>) => {
    return GoalsApi.saveGoal(projectId, {
      ...data,
      ...values,
    });
  });
  const { mutate: onDelete } = useMutation(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_: null) => {
      return GoalsApi.deleteGoal(data!.goalId!);
    },
    {
      retry: 0,
    },
  );
  const confirmDelete = useDeleteConfirmModal({
    title: useTranslation(
      'project.overview.business_goals.modal.delete.confirm_title',
    ),
    content: useTranslation(
      'project.overview.business_goals.modal.delete.confirm_message',
    ),
    onOk: () =>
      onDelete(null, {
        onError() {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
        },
        onSuccess() {
          queryClient.invalidateQueries(['project-overview-goals', projectId]);
          form.resetFields();
          onClose();
        },
      }),
  });

  const handleValidateAndSave = async () => {
    const values = await form.validateFields();

    onSave(values, {
      onError() {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      },
      onSuccess() {
        queryClient.invalidateQueries(['project-overview-goals', projectId]);
        form.resetFields();
        onClose();
      },
    });
  };

  return (
    <Modal
      confirmLoading={false}
      visible={visible}
      title={
        data ? (
          <FormattedMessage id="project.overview.business_goals.modal.title.edit" />
        ) : (
          <FormattedMessage id="project.overview.business_goals.modal.title.create" />
        )
      }
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      footer={
        <Footer>
          <ActionBar.Primary>
            {data?.goalId && (
              <Button danger onClick={confirmDelete}>
                <FormattedMessage id="delete" />
              </Button>
            )}
          </ActionBar.Primary>
          <ActionBar.Secondary>
            <Button type="link" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              icon={<Add16 />}
              iconPlacement="right"
              type="primary"
              onClick={handleValidateAndSave}
            >
              <FormattedMessage id="save" />
            </Button>
          </ActionBar.Secondary>
        </Footer>
      }
    >
      <Form form={form} layout="vertical" initialValues={data}>
        <Form.Item
          label={goalLabel}
          name="goalName"
          rules={[
            {
              required: true,
              message: goalErrorMessage,
            },
          ]}
        >
          <Input placeholder={goalPlaceholder} />
        </Form.Item>
        <Form.Item
          label={goalDescriptionLabel}
          name="goalDescription"
          rules={[
            {
              required: true,
              message: goalDescriptionErrorMessage,
            },
          ]}
        >
          <RichTextareaEditor
            data={
              form.getFieldValue('goalDescription') ||
              data?.goalDescription ||
              ''
            }
            placeholder={goalDescriptionPlaceholder}
            onChange={(goalDescription) =>
              form.setFieldsValue({
                goalDescription,
              })
            }
            disallowImages
            projectId={ProjectId(projectId)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
